import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { InitialsAvatar } from "@/components/InitialsAvatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCambiarEstadoDeComentario } from "@/hooks/useFeedback";
import { getApiErrorMessage } from "@/lib/api";
import { formatearMomento } from "@/lib/fechas";
import type { ComentarioDeFeedback, OpcionDeFeedback } from "@/lib/feedback";

/**
 * Color del badge según el tipo. Es presentación pura —el rótulo lo escribe el
 * backend— y por eso vive acá y no en el API.
 *
 * `default` como salida: si mañana nace un tipo nuevo, la fila se dibuja con
 * el badge neutro en vez de romperse. Un `Record` exhaustivo daría
 * `undefined` en silencio.
 */
function variantePorTipo(tipo: string): "success" | "warning" | "info" | "default" {
  switch (tipo) {
    case "problema":
      return "warning";
    case "sugerencia":
      return "success";
    case "consulta":
      return "info";
    default:
      return "default";
  }
}

/**
 * Color del badge según el estado del trabajo.
 *
 * `pendiente` va NEUTRO a propósito: es la ausencia de decisión, y pintarlo de
 * un color lo haría parecer un estado tan activo como los otros dos.
 */
function variantePorEstado(estado: string): "success" | "info" | "default" {
  switch (estado) {
    case "en_desarrollo":
      return "info";
    case "desarrollado":
      return "success";
    default:
      return "default";
  }
}

interface TarjetaDeComentarioProps {
  comentario: ComentarioDeFeedback;
  /** Las opciones del selector de estado (solo se usan si se puede cambiar). */
  estados: OpcionDeFeedback[];
  onEditar: (comentario: ComentarioDeFeedback) => void;
  onEliminar: (comentario: ComentarioDeFeedback) => void;
}

/**
 * Una fila del tablero: quién, sobre qué, de qué tipo, en qué anda y el texto.
 *
 * Qué acciones se dibujan sale de los `puede_*` que manda el servidor, no de
 * una regla escrita acá. Esos campos los responde la MISMA Policy que
 * autoriza cada request, así que la tarjeta no puede ofrecer un botón que
 * termine en un 403. Son para dibujar: la autorización real la sigue haciendo
 * el backend.
 */
export function TarjetaDeComentario({
  comentario,
  estados,
  onEditar,
  onEliminar,
}: TarjetaDeComentarioProps) {
  const cambiarEstado = useCambiarEstadoDeComentario();
  const hayAcciones = comentario.puede_editar || comentario.puede_eliminar;

  return (
    <article className="bg-card rounded-xl border p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <InitialsAvatar nombre={comentario.autor.nombre} className="size-8" />
          <div className="leading-tight">
            <p className="text-sm font-bold">{comentario.autor.nombre}</p>
            <p className="text-ink-hint text-xs">{comentario.modulo_etiqueta}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge variant={variantePorTipo(comentario.tipo)}>{comentario.tipo_etiqueta}</Badge>

          {comentario.puede_cambiar_estado ? (
            /* Selector en vez de badge: para quien puede triar, el estado no
               es información sino un control. Dibujar los dos sería decir dos
               veces lo mismo y dejar dudando cuál manda. */
            <Select
              value={comentario.estado}
              disabled={cambiarEstado.isPending}
              onValueChange={(estado) =>
                cambiarEstado.mutate(
                  { id: comentario.id, estado },
                  {
                    onError: (error) =>
                      toast.error(
                        getApiErrorMessage(
                          error,
                          "No pudimos cambiar el estado. Inténtalo de nuevo.",
                        ),
                      ),
                  },
                )
              }
            >
              <SelectTrigger size="sm" aria-label="Estado del comentario">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {estados.map((estado) => (
                  <SelectItem key={estado.valor} value={estado.valor}>
                    {estado.etiqueta}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Badge variant={variantePorEstado(comentario.estado)}>
              {comentario.estado_etiqueta}
            </Badge>
          )}

          <span className="text-ink-hint text-xs font-medium">
            {formatearMomento(comentario.created_at)}
          </span>

          {/* Botones EN LÍNEA y no un menú de tres puntos: son dos acciones
              como mucho, y el proyecto no usa menús contextuales en ninguna
              pantalla (los catálogos también editan y borran con un par de
              íconos por fila). Un patrón de interacción nuevo para ahorrar un
              botón no se paga.

              El `aria-label` nombra al AUTOR: con varias tarjetas en pantalla,
              "Editar" a secas se repite y ni un lector de pantalla ni un test
              pueden distinguir cuál es cuál. */}
          {hayAcciones && (
            <div className="flex items-center gap-1">
              {comentario.puede_editar && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Editar el comentario de ${comentario.autor.nombre}`}
                  onClick={() => onEditar(comentario)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
              {comentario.puede_eliminar && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Eliminar el comentario de ${comentario.autor.nombre}`}
                  onClick={() => onEliminar(comentario)}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* `whitespace-pre-line`: la gente escribe en párrafos y el salto de
          línea que puso es parte de lo que quiso decir. Sin esto el comentario
          se aplana en un bloque corrido. */}
      <p className="text-foreground/90 mt-3 text-sm whitespace-pre-line">{comentario.texto}</p>
    </article>
  );
}
